// Purpose-specific render for the `create_issue_with_link_proposals`
// proposal. Phase 10b.
//
// Layout:
//   - draft preview (title + labels + description)
//   - candidate selector (SelectorBlock with edge-type pickers)
//   - bulk confirm / reject buttons
//
// The user picks which proposed links to keep — confirm fires once with the
// chosen subset (the parent threads the choice into `proposal_confirmed`).
// Phase 12 polishes further (per-row scoring summaries, draft inline edit).

import type { Proposal } from "@loom/shared";

import { useT } from "../../i18n/index.js";
import { useRemountSafeState } from "../../state/remount-safe-state";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { SelectorBlock, type SelectorCandidate } from "./SelectorBlock";

type CreateIssueProposalShape = Extract<
  Proposal,
  { kind: "create_issue_with_link_proposals" }
>;

interface CreateIssueProposalProps {
  readonly proposalId: string;
  readonly proposal: CreateIssueProposalShape;
  readonly state: "pending" | "confirmed" | "rejected";
  readonly onConfirm: (
    proposalId: string,
    selectedLinks: ReadonlyArray<SelectorCandidate>,
  ) => void;
  readonly onReject: (proposalId: string) => void;
}

export function CreateIssueProposal({
  proposal,
  proposalId,
  state,
  onConfirm,
  onReject,
}: CreateIssueProposalProps) {
  const t = useT();
  // Remount-safe, keyed by the server-minted proposalId (stable across the
  // reconnect replay): the wipe must not discard a confirmed link pick nor
  // re-open the selector step. The outer confirmed/rejected state needs no
  // such treatment — `state` is server-owned and folds back in on replay.
  const [selectedLinks, setSelectedLinks] = useRemountSafeState<
    ReadonlyArray<SelectorCandidate>
  >(`proposal:${proposalId}:links`, []);
  const [selectorConfirmed, setSelectorConfirmed] = useRemountSafeState(
    `proposal:${proposalId}:selectorConfirmed`,
    proposal.candidates.length === 0,
  );

  const selectorCandidates: ReadonlyArray<SelectorCandidate> =
    proposal.candidates.map((c) => ({
      targetId: c.targetId,
      edgeType: c.edgeType,
      rationale: c.rationale,
    }));

  // No border or background recolour here: Card's own surface/line utilities
  // sort later in the built CSS than any info-family one, so an override passed
  // in className never rendered — the card has always drawn as a plain surface
  // card. Only the text colour applies (Card sets none).
  return (
    <Card className="space-y-3 p-3 text-xs text-info-fg-3">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          {t("chatRender.newIssue")}
        </div>
        <div className="text-sm font-medium text-ink">
          {proposal.draft.title}
        </div>
        {proposal.draft.labels.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {proposal.draft.labels.map((label) => (
              <span
                key={label}
                className="rounded bg-surface-4 px-1.5 py-0.5 font-mono text-[10px] text-ink-3"
              >
                {label}
              </span>
            ))}
          </div>
        )}
        {proposal.draft.description && (
          <pre className="mt-2 whitespace-pre-wrap rounded border border-line bg-surface p-2 text-[11px] text-ink-3">
            {proposal.draft.description}
          </pre>
        )}
      </div>

      {selectorCandidates.length > 0 && !selectorConfirmed && (
        <div>
          <SelectorBlock
            candidates={selectorCandidates}
            editableEdgeType
            stateKey={`proposal:${proposalId}:selector`}
            onConfirm={(picked) => {
              setSelectedLinks(picked);
              setSelectorConfirmed(true);
            }}
          />
        </div>
      )}

      {state === "pending" && selectorConfirmed && (
        <div className="space-y-2">
          <div className="text-ink-3">
            {selectedLinks.length === 0
              ? t("chatRender.confirmNoLinks")
              : // FLAGGED: inline English plural ternary ("apply N link(s)") —
                // no fitting plural.* key, plurals.ts owned elsewhere.
                `Confirm to create the issue and apply ${selectedLinks.length} link${selectedLinks.length === 1 ? "" : "s"}.`}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => onConfirm(proposalId, selectedLinks)}>
              {t("common.confirm")}
            </Button>
            <button
              type="button"
              className="rounded border border-line-strong px-2 py-1 hover:bg-surface-3"
              onClick={() => onReject(proposalId)}
            >
              {t("common.reject")}
            </button>
          </div>
        </div>
      )}

      {state === "confirmed" && (
        <div className="text-success-fg">{t("chatRender.applied")}</div>
      )}
      {state === "rejected" && <div className="text-ink-subtle">{t("chatRender.rejected")}</div>}
    </Card>
  );
}

export type { CreateIssueProposalProps };
